import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { socket } from "../../utils/socket";

const Invite: React.FC = () => {
    const location = useLocation();
    const [hasAccepted, setHasAccepted] = useState(false);
    const [status, setStatus] = useState<"idle" | "joining" | "accepted" | "error">("idle");

    const searchParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const participantId = searchParams.get("participant_id");
    const hostId = searchParams.get("host_id");
    const hostName = searchParams.get("host_name");

    const isInvalidLink = !participantId || !hostId;

    useEffect(() => {
        if (isInvalidLink) {
            setStatus("error");
            return;
        }

        setStatus("joining");

        const handleJoinedRoom = (data: any) => {
            // When backend confirms room join, accept the invitation
            if (!data) return;
            // Optionally you can verify room or ids here if backend sends them

            socket.emit("participant_joined", {
                participant_id: participantId,
                host_id: hostId,
            });
            setHasAccepted(true);
            setStatus("accepted");
        };

        // Ask backend to join the room as participant (non-host)


        socket.on("joined_room", handleJoinedRoom);

        socket.on("connect", () => {
            socket.emit("join_room", {
                user_id: participantId,
                host_id: hostId,
            });
        });

        if (socket.connected && participantId && hostId) {
            socket.emit("join_room", {
                user_id: participantId,
                host_id: hostId,
            });
        }

        return () => {
            socket.off("joined_room", handleJoinedRoom);
        };
    }, [isInvalidLink, participantId, hostId, socket.connected]);

    return (
        <div className="min-h-screen flex items-center justify-center 2xl:px-[0.833vw] xl:px-10 px-4 2xl:py-[2.083vw] md:py-10 py-6 bg-primary-ivory">
            <div className="relative 2xl:max-w-[41.667vw] max-w-[800px] w-full bg-white-500 2xl:rounded-[1.5vw] rounded-3xl 2xl:border-[0.2vw] border-3 border-primary 2xl:shadow-[0px_0.521vw_0px_#C3DC78] shadow-[0px_10px_0px_#C3DC78] 2xl:p-[2vw] md:p-8 p-6 text-center overflow-hidden">
                {/* Decorative Elements */}
                <div className="pointer-events-none absolute 2xl:-top-[2.083vw] -top-10 2xl:-start-[3.333vw] -start-16 2xl:w-[8.333vw] w-40 2xl:h-[8.333vw] h-40 rounded-full bg-primary-yellow opacity-30 blur-2xl" />
                <div className="pointer-events-none absolute 2xl:-bottom-[3.333vw] -bottom-16 2xl:-end-[2.083vw] -end-10 2xl:w-[10vw] w-48 2xl:h-[10vw] h-48 rounded-full bg-primary-orange opacity-20 blur-3xl" />

                {/* Title */}
                <h1 className="relative font-garamond font-bold text-primary-orange 2xl:text-[3.125vw]/tight xl:text-5xl/tight md:text-4xl/tight text-3xl/tight 2xl:mb-[1.25vw] md:mb-6 mb-4 text-stroke-sm">
                    {hostName ? `You're Invited by ${hostName}!` : "You're Invited to Play!"}
                </h1>

                {/* Invalid Link Message */}
                {isInvalidLink && (
                    <div className="relative 2xl:mt-[1.667vw] mt-8">
                        <div className="2xl:p-[1.25vw] md:p-6 p-4 bg-red-50 2xl:rounded-[0.625vw] rounded-xl 2xl:border-[0.104vw] border-2 border-red-300">
                            <p className="text-red-600 font-bold 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight 2xl:mb-[0.417vw] mb-2">
                                Invalid Invitation Link
                            </p>
                            <p className="text-red-500 font-semibold 2xl:text-[0.938vw]/tight text-base/tight">
                                This invitation link is invalid or incomplete.
                            </p>
                        </div>
                    </div>
                )}

                {/* Joining State */}
                {!isInvalidLink && status === "joining" && (
                    <div className="relative 2xl:mt-[2.083vw] mt-10 flex flex-col items-center 2xl:gap-[1.25vw] gap-6">
                        <div className="2xl:w-[3.125vw] 2xl:h-[3.125vw] w-16 h-16 2xl:border-[0.208vw] border-4 border-primary-orange border-t-transparent rounded-full animate-spin" />
                        <div className="2xl:p-[1.042vw] md:p-5 p-4 bg-primary-ivory 2xl:rounded-[0.625vw] rounded-xl 2xl:border-[0.104vw] border-2 border-black-300 2xl:shadow-[0px_0.208vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                            <p className="text-black-300 font-bold 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight">
                                Joining room, please wait...
                            </p>
                        </div>
                    </div>
                )}

                {/* Accepted State */}
                {!isInvalidLink && hasAccepted && status === "accepted" && (
                    <div className="relative 2xl:mt-[2.083vw] mt-10">
                        <div className="2xl:p-[1.667vw] md:p-8 p-6 bg-primary 2xl:rounded-[0.625vw] rounded-xl 2xl:border-[0.104vw] border-2 border-black-300 2xl:shadow-[0px_0.208vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                            {/* Success Icon */}
                            <div className="2xl:w-[4.167vw] 2xl:h-[4.167vw] w-20 h-20 mx-auto 2xl:mb-[1.042vw] mb-5 bg-primary-yellow 2xl:rounded-[5vw] rounded-full flex items-center justify-center 2xl:border-[0.208vw] border-4 border-black-300 2xl:shadow-[0px_0.208vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                                <svg className="2xl:w-[2.083vw] 2xl:h-[2.083vw] w-10 h-10 text-black-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-black-300 font-bold 2xl:text-[1.563vw]/tight xl:text-3xl/tight text-2xl/tight 2xl:mb-[0.625vw] mb-3">
                                Invitation Accepted!
                            </p>
                            <p className="text-black-800 font-semibold 2xl:text-[1.042vw]/tight xl:text-lg/tight text-base/tight">
                                You've successfully joined the game. Please wait for the host to start.
                            </p>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {status === "error" && !isInvalidLink && (
                    <div className="relative 2xl:mt-[2.083vw] mt-10">
                        <div className="2xl:p-[1.25vw] md:p-6 p-4 bg-red-50 2xl:rounded-[0.625vw] rounded-xl 2xl:border-[0.104vw] border-2 border-red-300">
                            <p className="text-red-600 font-bold 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight 2xl:mb-[0.625vw] mb-3">
                                Oops, something went wrong!
                            </p>
                            <p className="text-red-500 font-semibold 2xl:text-[0.938vw]/tight text-base/tight">
                                Please check your invitation link or try again later.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Invite;
